import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTenant } from '@/lib/auth/tenant-context';
import {
  createCategoryCollection,
  type CategoryCollection,
} from '@/api/category/collection';
import {
  createCompanyCollection,
  type CompanyCollection,
} from '@/api/company/collection';
import {
  createAccountCollection,
  type AccountCollection,
} from '@/api/account/collection';
import {
  createUserMeCollection,
  type UserMeCollection,
} from '@/api/user/collection';

interface MasterDataContextValue {
  categories: CategoryCollection | null;
  companies: CompanyCollection | null;
  accounts: AccountCollection | null;
  userMe: UserMeCollection | null;
}

const MasterDataContext = createContext<MasterDataContextValue>({
  categories: null,
  companies: null,
  accounts: null,
  userMe: null,
});

export function MasterDataProvider({ children }: { children: ReactNode }) {
  const { selectedTenant } = useTenant();
  const queryClient = useQueryClient();

  const collections = useMemo(() => {
    if (!selectedTenant?.id) {
      return {
        categories: null,
        companies: null,
        accounts: null,
        userMe: null,
      };
    }
    return {
      categories: createCategoryCollection(selectedTenant.id, queryClient),
      companies: createCompanyCollection(selectedTenant.id, queryClient),
      accounts: createAccountCollection(selectedTenant.id, queryClient),
      userMe: createUserMeCollection(selectedTenant.id, queryClient),
    };
  }, [selectedTenant?.id, queryClient]);

  return (
    <MasterDataContext.Provider value={collections}>
      {children}
    </MasterDataContext.Provider>
  );
}

export const useMasterData = () => useContext(MasterDataContext);

export const useCategoryCollection = () => {
  const { categories } = useMasterData();
  if (!categories) throw new Error('No tenant selected');
  return categories;
};

export const useCompanyCollection = () => {
  const { companies } = useMasterData();
  if (!companies) throw new Error('No tenant selected');
  return companies;
};

export const useAccountCollection = () => {
  const { accounts } = useMasterData();
  if (!accounts) throw new Error('No tenant selected');
  return accounts;
};
